import { BehaviorPattern } from './../../models/behavior-pattern';
import { Injectable } from '@angular/core';
import { GenericHttpClientService } from 'core-services';
import { Observable } from 'rxjs';
import { API } from '@app/core/api';
import { ISetting } from '@app/models/setting';
import { SensorMCUProximity } from '@app/models/sensor-mcu-proximity';
import { Sensor } from '@app/models/device-sensor';
import { IAlertClassification } from '@app/models/alert-classification'
import { IFlow } from '@app/models/flow';
import { IFlowDetails, IFlowStep } from '@app/models/flow-step';
import { IUserGroup, IUserGroupMemberUser } from '@app/models/user-group';

const SystemParametersApiUrl: string = API.systemParametersApiUrl;
const SystemSettingsApiUrl: string = API.systemSettingsApiUrl;
const SimulatorApiUrl: string = API.testingApiUrl.simulator;
const ExternalDeviceSimulatorApiUrl: string = API.testingApiUrl.externalDeviceSimulator;
const BehaviorHistory: string = API.behaviorHistory;
const RetrieveMissingEventsApiUrl: string = API.testingApiUrl.retrieveMissingEvents;
const AlertNotificationHistory: string = API.AlertNotificationHistoryUrl;
const BehaviorPatternUpdate: string = API.behaviorPatternsUpdate;
const addUpdateSensorProximityList = API.sensorMCUPrximityAddUpdate
const updateDeviceSensors = API.updateDeviceSensors
const LoggerApiUrl: string = API.loggerApiUrl;
const AlertClassificationUrl: string = API.alertClassification;
const FlowsApiUrl: string = API.flowsApiUrl;
const UserGroupsApiUrl: string = API.userGroupsApiUrl;

@Injectable({
    providedIn: 'root',
})
export class GlobalService extends GenericHttpClientService<any, string> {
    //#region  ################  System Parameters  ################
    /**
     * read the system parameters from API
     * @returns Observable
     */
    readSystemParameters(accountId: string): Observable<ISetting[]> {
        return super.readOne(`${SystemParametersApiUrl}/${accountId}`);
    }

    /**
     * update the system parameters by API
     * @returns Observable
     */
    updateSystemParameters(items: ISetting[], accountId :string): Observable<ISetting[]> {
        return super.update(`${SystemParametersApiUrl}/${accountId}`, items);
    }

    /**
     * read the alert classifications from API
     * @returns Observable
     */
    readAlertClassifications(accountId: string): Observable<IAlertClassification[]> {   
        return super.readList(`${AlertClassificationUrl}/${accountId}`);
    }

    /**
     * update the alert classifications by API
     * @returns Observable
     */
    updateAlertClassifications(items: IAlertClassification[], accountId :string): Observable<IAlertClassification[]> {
        return super.update(`${AlertClassificationUrl}/${accountId}`, items);
    }

    /**
     * update the system parameters by API
     * @returns Observable
     */
    updateSystemSettings(items: ISetting[]): Observable<ISetting[]> {
        return super.update(SystemSettingsApiUrl, items);
    }

    //#endregion  ################  System Parameters  ################

    //#region  ################  Flows  ################

    /**
     * read the active flows of an account from API
     * @param accountId the account identifier
     * @returns Observable
     */
    readFlows(accountId: string): Observable<IFlow[]> {
        return super.readList(`${FlowsApiUrl}/accounts/${accountId}`);
    }

    /**
     * read a single flow together with its active steps from API
     * @param flowId the flow identifier
     * @returns Observable
     */
    readFlow(flowId: string): Observable<IFlowDetails> {
        return super.readOne(`${FlowsApiUrl}/${flowId}`);
    }

    /**
     * create a flow for an account
     * @param accountId the account identifier
     * @param flow the flow
     * @returns Observable
     */
    createFlow(accountId: string, flow: IFlow): Observable<IFlow> {
        return super.create(`${FlowsApiUrl}/accounts/${accountId}`, flow);
    }

    /**
     * update a flow's name/description
     * @param flowId the flow identifier
     * @param flow the flow
     * @returns Observable
     */
    updateFlow(flowId: string, flow: IFlow): Observable<IFlow> {
        return super.update(`${FlowsApiUrl}/${flowId}`, flow);
    }

    /**
     * soft-delete a flow
     * @param flowId the flow identifier
     * @returns Observable
     */
    deleteFlow(flowId: string): Observable<any> {
        return super.delete(`${FlowsApiUrl}/${flowId}`);
    }

    /**
     * add a step to a flow
     * @param flowId the flow identifier
     * @param step the step
     * @returns Observable
     */
    createFlowStep(flowId: string, step: IFlowStep): Observable<IFlowStep> {
        return super.create(`${FlowsApiUrl}/${flowId}/steps`, step);
    }

    /**
     * update a step's description/required flag
     * @param flowId the flow identifier
     * @param stepId the step identifier
     * @param step the step
     * @returns Observable
     */
    updateFlowStep(flowId: string, stepId: string, step: IFlowStep): Observable<IFlowStep> {
        return super.update(`${FlowsApiUrl}/${flowId}/steps/${stepId}`, step);
    }

    /**
     * soft-delete a step
     * @param flowId the flow identifier
     * @param stepId the step identifier
     * @returns Observable
     */
    deleteFlowStep(flowId: string, stepId: string): Observable<any> {
        return super.delete(`${FlowsApiUrl}/${flowId}/steps/${stepId}`);
    }

    /**
     * reorder a flow's steps
     * @param flowId the flow identifier
     * @param orderedStepIds the step identifiers in the desired order
     * @returns Observable
     */
    reorderFlowSteps(flowId: string, orderedStepIds: string[]): Observable<any> {
        return super.update(`${FlowsApiUrl}/${flowId}/steps/reorder`, orderedStepIds as any);
    }

    //#endregion  ################  Flows  ################

    //#region  ################  User Groups  ################

    /**
     * read the active user groups for an account from API
     * @param accountId the account identifier
     * @returns Observable of IUserGroup[]
     */
    readUserGroups(accountId: string): Observable<IUserGroup[]> {
        return super.readList(`${UserGroupsApiUrl}/accounts/${accountId}`);
    }

    /**
     * create a user group for an account
     * @param accountId the account identifier
     * @param group the group
     * @returns Observable of IUserGroup
     */
    createUserGroup(accountId: string, group: IUserGroup): Observable<IUserGroup> {
        return super.create(`${UserGroupsApiUrl}/accounts/${accountId}`, group);
    }

    /**
     * update a user group's name/description/CanHandleAlerts flag
     * @param group the group
     * @returns Observable of IUserGroup
     */
    updateUserGroup(group: IUserGroup): Observable<IUserGroup> {
        return super.update(`${UserGroupsApiUrl}`, group);
    }

    /**
     * soft-delete a user group
     * @param groupId the group identifier
     * @returns Observable
     */
    deleteUserGroup(groupId: string): Observable<any> {
        return super.delete(`${UserGroupsApiUrl}/${groupId}`);
    }

    /**
     * read the members of a user group
     * @param groupId the group identifier
     * @returns Observable of IUserGroupMemberUser[]
     */
    readUserGroupMembers(groupId: string): Observable<IUserGroupMemberUser[]> {
        return super.readList(`${UserGroupsApiUrl}/${groupId}/members`);
    }

    /**
     * add a user to a group
     * @param groupId the group identifier
     * @param userId the user identifier
     * @returns Observable
     */
    addUserGroupMember(groupId: string, userId: string): Observable<any> {
        return super.create(`${UserGroupsApiUrl}/${groupId}/members/${userId}`, null);
    }

    /**
     * add multiple users to a group in one call
     * @param groupId the group identifier
     * @param userIds the user identifiers
     * @returns Observable
     */
    addUserGroupMembers(groupId: string, userIds: string[]): Observable<any> {
        return super.create(`${UserGroupsApiUrl}/${groupId}/members`, userIds);
    }

    /**
     * remove a user from a group
     * @param groupId the group identifier
     * @param userId the user identifier
     * @returns Observable
     */
    removeUserGroupMember(groupId: string, userId: string): Observable<any> {
        return super.delete(`${UserGroupsApiUrl}/${groupId}/members/${userId}`);
    }

    /**
     * checks whether the logged-in user belongs to a group eligible to handle alerts for an account
     * @param accountId the account identifier
     * @returns Observable of boolean
     */
    isEligibleToHandleAlerts(accountId: string): Observable<boolean> {
        return super.readOne(`${UserGroupsApiUrl}/accounts/${accountId}/eligible-to-handle-alerts`);
    }

    /**
     * gets the ids of the groups (within the given account) that a user belongs to
     * @param accountId the account identifier
     * @param userId the user identifier
     * @returns Observable of string[]
     */
    getUserGroupIdsForUser(accountId: string, userId: string): Observable<string[]> {
        return super.readList(`${UserGroupsApiUrl}/accounts/${accountId}/users/${userId}`);
    }

    /**
     * gets the users that the logged-in user is allowed to assign alerts to for an account
     * @param accountId the account identifier
     * @returns Observable of IUserGroupMemberUser[]
     */
    getMyAssignableUsers(accountId: string): Observable<IUserGroupMemberUser[]> {
        return super.readList(`${UserGroupsApiUrl}/accounts/${accountId}/my-assignable-users`);
    }

    //#endregion  ################  User Groups  ################

    //#region  ################  simulator  ################

    /**
     * send simulated event
     * @param data the event data
     * @returns api's endpoint response
     */
    sendSimulatesDeviceEvent(data): Observable<any> {
        return super.create(SimulatorApiUrl, data);
    }
    /**
     * send simulated event
     * @param data the event data
     * @returns api's endpoint response
     */
    sendSimulatedExternalDeviceEvent(paramsString): Observable<any> {
        return super.readList(ExternalDeviceSimulatorApiUrl + paramsString);
    }

    /**
     * Retrieve missing events
     * @param data the event data
     * @returns api's endpoint response
     */
    retrieveMissingDeviceEvents(paramsString): Observable<any> {
        return super.create(RetrieveMissingEventsApiUrl, paramsString);
    }

    /**
     * Read Sense Events
     * @param paramsString the params string
     * @returns Observable of api's endpoint response
     */
    readSenseEvents(paramsString: string): Observable<any> {
        return super.readList(SimulatorApiUrl + paramsString);
    }

    /**
     * Read Behavior History Events
     * @param paramsString the params string
     * @returns Observable of api's endpoint response
     */
    readBehaviorHistoryEvents(paramsString: string): Observable<any> {
        return super.readList(BehaviorHistory + paramsString);
    }

    /**
     * Read Alert Notification Events
     * @param accountId the accountId string
     * @returns Observable of api's endpoint response
     */
    readAlertNotificationHistoryEvents(accountId: string): Observable<any> {
        return super.readList(`${AlertNotificationHistory}/${accountId}`);
    }

    /**
     * update the subscriber behavior patterns by API
     * @returns Observable
     */
    updateBehaviorPatterns(items: BehaviorPattern[]): Observable<BehaviorPattern[]> {
        return super.update(BehaviorPatternUpdate, items);
    }

    /**
     * Adds/Updates Sensor-MCU Proximity List
     * @param items SensorMCUProximity[]
     * @returns Observable
     */
    addUpdateSensorProximityList(items: SensorMCUProximity[]) : Observable<SensorMCUProximity[]> {
        return super.create(addUpdateSensorProximityList, items);
    }

    /**
     * Adds/Updates Sensor-MCU Proximity List
     * @param items SensorMCUProximity[]
     * @returns Observable
     */
    updateDeviceSensors(items: Sensor[]) : Observable<Sensor[]> {
        return super.update(updateDeviceSensors, items);
    }

    //#endregion  ################  simulator  ################

    /**
     * read the server's logs from API
     * @returns Observable
     */
    readLogs(): Observable<any> {
        return super.readOne(LoggerApiUrl);
    }
}
